/**
 * CallStats — periodically samples WebRTC getStats() during a call and logs a
 * compact line about OUTBOUND audio quality (what the customer hears from the
 * rep). This exists because reps reported customers "can't hear them well":
 * outbound choppiness is almost always packet loss / a bad media path, and the
 * only way to tell that apart from a codec ceiling is to measure it.
 *
 * Logged per sample (console.info, prefixed [callstats]):
 *  - loss%   : outbound packets lost / sent since the last sample. High or
 *              spiky loss ⇒ media path problem (needs TURN or a better route).
 *  - rtt     : round-trip time (s) from the selected candidate pair.
 *  - jitter  : inbound jitter (s) as a secondary path-health signal.
 *  - codec   : the negotiated outbound audio codec + clock rate.
 *  - relay   : whether the SELECTED ICE path is a TURN relay ('relay') or
 *              host/srflx (direct/STUN). If audio is bad AND relay is not
 *              'relay' on a strict network, that's the missing-TURN smoking gun.
 *
 * Sampling is best-effort and self-cleaning; it never throws into the call.
 */

interface Prev {
  packetsSent: number;
  packetsLost: number;
  ts: number;
}

/** Compact end-of-call summary, sent to the server for cross-call review. */
export interface AudioStatsSummary {
  lossAvg: number;   // mean outbound loss % across samples
  lossMax: number;   // worst single-sample loss %
  rttMs: number;     // last observed round-trip (ms)
  jitterMs: number;  // last observed jitter (ms)
  path: string;      // selected ICE path: relay | srflx | host | ?
  codec: string;     // negotiated outbound codec
  samples: number;   // how many samples fed the averages
}

export class CallStats {
  private timer: number | null = null;
  private prev: Prev | null = null;
  // Running aggregates for the end-of-call summary.
  private lossSum = 0;
  private lossCount = 0;
  private lossMax = 0;
  private lastRttMs = NaN;
  private lastJitterMs = NaN;
  private lastPath = '?';
  private lastCodec = '?';

  /** Start sampling every `intervalMs` (default 5s) for the given connection. */
  start(pc: RTCPeerConnection, intervalMs = 5000) {
    if (this.timer !== null) return;
    const tick = () => {
      this.sample(pc).catch(() => {/* best-effort */});
    };
    this.timer = window.setInterval(tick, intervalMs);
    // First sample shortly after media should be flowing.
    window.setTimeout(tick, 1500);
  }

  private async sample(pc: RTCPeerConnection) {
    if (!pc || typeof pc.getStats !== 'function') return;
    const report = await pc.getStats();

    let outbound: any = null;
    let remoteInbound: any = null;
    let inbound: any = null;
    let selectedPair: any = null;
    const codecs = new Map<string, any>();
    const candidates = new Map<string, any>();

    report.forEach((s: any) => {
      if (s.type === 'outbound-rtp' && s.kind === 'audio') outbound = s;
      else if (s.type === 'remote-inbound-rtp' && s.kind === 'audio') remoteInbound = s;
      else if (s.type === 'inbound-rtp' && s.kind === 'audio') inbound = s;
      else if (s.type === 'candidate-pair' && (s.selected || s.nominated) && s.state === 'succeeded') selectedPair = s;
      else if (s.type === 'codec') codecs.set(s.id, s);
      else if (s.type === 'local-candidate' || s.type === 'remote-candidate') candidates.set(s.id, s);
    });

    // Outbound loss is reported by the FAR side via remote-inbound-rtp
    // (packetsLost there = our packets the customer never received).
    const packetsSent = Number(outbound?.packetsSent ?? NaN);
    const packetsLost = Number(remoteInbound?.packetsLost ?? NaN);

    let lossPct = NaN;
    if (this.prev && Number.isFinite(packetsSent) && Number.isFinite(packetsLost)) {
      const dSent = packetsSent - this.prev.packetsSent;
      const dLost = packetsLost - this.prev.packetsLost;
      const denom = dSent + dLost;
      if (denom > 0) lossPct = Math.max(0, (dLost / denom) * 100);
    }
    if (Number.isFinite(packetsSent) && Number.isFinite(packetsLost)) {
      this.prev = { packetsSent, packetsLost, ts: Date.now() };
    }

    const rtt = Number(remoteInbound?.roundTripTime ?? selectedPair?.currentRoundTripTime ?? NaN);
    const jitter = Number(remoteInbound?.jitter ?? inbound?.jitter ?? NaN);

    const codec = outbound && codecs.get(outbound.codecId);
    const codecLabel = codec ? `${String(codec.mimeType || '').replace('audio/', '')}/${codec.clockRate}` : '?';

    // Is the selected path a TURN relay?
    const localCand = selectedPair && candidates.get(selectedPair.localCandidateId);
    const relay = localCand?.candidateType === 'relay' ? 'relay'
      : localCand?.candidateType ? localCand.candidateType : '?';

    // Feed the running aggregates (only when we computed a real loss figure).
    if (Number.isFinite(lossPct)) {
      this.lossSum += lossPct;
      this.lossCount += 1;
      if (lossPct > this.lossMax) this.lossMax = lossPct;
    }
    if (Number.isFinite(rtt)) this.lastRttMs = rtt * 1000;
    if (Number.isFinite(jitter)) this.lastJitterMs = jitter * 1000;
    if (relay !== '?') this.lastPath = relay;
    if (codecLabel !== '?') this.lastCodec = codecLabel;

    const fmt = (n: number, unit = '') => (Number.isFinite(n) ? `${n.toFixed(unit === 'ms' ? 0 : 1)}${unit}` : '?');
    // eslint-disable-next-line no-console
    console.info(
      `[callstats] out-loss=${fmt(lossPct, '%')} rtt=${fmt(rtt * 1000, 'ms')} ` +
      `jitter=${fmt(jitter * 1000, 'ms')} codec=${codecLabel} path=${relay}`,
    );
  }

  /** End-of-call summary for the server, or null if nothing was sampled. */
  summary(): AudioStatsSummary | null {
    if (this.lossCount === 0 && this.lastPath === '?') return null;
    const round = (n: number, d = 1) => (Number.isFinite(n) ? Number(n.toFixed(d)) : 0);
    return {
      lossAvg: round(this.lossCount ? this.lossSum / this.lossCount : 0),
      lossMax: round(this.lossMax),
      rttMs: round(this.lastRttMs, 0),
      jitterMs: round(this.lastJitterMs, 0),
      path: this.lastPath,
      codec: this.lastCodec,
      samples: this.lossCount,
    };
  }

  stop() {
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
    this.prev = null;
  }
}
